/**
 * API Error Handling Composable
 *
 * Provides utilities for handling API errors, extracting error messages,
 * and managing error responses.
 */

import type {
  ApiV1Response,
  ApiErrorInfo,
  RouterInterface,
  NotifyInterface,
  I18nInterface,
  PaginationMeta,
  ListResponse,
} from '@shared/core/types/api';

/**
 * Composable for API error handling
 */
export function useApiErrorHandling() {
  // ==========================================
  // Type Guards
  // ==========================================

  /**
   * Checks if the data is an API error response (status === 'error').
   *
   * The predicate narrows to `{ status: 'error' }` so that callers in
   * the success branch keep access to the original response shape —
   * before this was just `data is ApiV1Response`, which collapsed the
   * negative branch to `never` once the per-app and shared envelope
   * types were unified.
   */
  const isApiError = (data: unknown): data is ApiV1Response & { status: 'error' } => {
    const obj = data as Record<string, unknown>;

    // Basic structure check
    const hasBasicStructure =
      data !== null &&
      typeof data === 'object' &&
      'status' in obj &&
      obj.status === 'error' &&
      'message' in obj &&
      (obj.message === null || typeof obj.message === 'string') &&
      'version' in obj;

    if (!hasBasicStructure) return false;

    // Check optional error properties if they exist
    if ('error_type' in obj && typeof obj.error_type !== 'string') {
      return false;
    }

    if ('errors' in obj) {
      const errors = obj.errors;
      if (errors === null || typeof errors !== 'object') {
        return false;
      }

      // Check if errors is a Record<string, string[]>
      for (const key in errors) {
        const value = (errors as Record<string, unknown>)[key];
        if (!Array.isArray(value) || !value.every((item) => typeof item === 'string')) {
          return false;
        }
      }
    }

    return true;
  };

  /**
   * Checks if the data is a valid API response
   */
  const isApiV1Response = (data: unknown): data is ApiV1Response => {
    const obj = data as Record<string, unknown>;

    // Basic structure check
    const hasBasicStructure =
      data !== null &&
      typeof data === 'object' &&
      'status' in obj &&
      (obj.status === 'success' || obj.status === 'error') &&
      'message' in obj &&
      (obj.message === null || typeof obj.message === 'string') &&
      'version' in obj &&
      'data' in obj;

    if (!hasBasicStructure) return false;

    // Check optional error properties if they exist
    if ('error_type' in obj && typeof obj.error_type !== 'string') {
      return false;
    }

    if ('errors' in obj) {
      const errors = obj.errors;
      if (errors === null || typeof errors !== 'object') {
        return false;
      }

      // Check if errors is a Record<string, string[]>
      for (const key in errors) {
        const value = (errors as Record<string, unknown>)[key];
        if (!Array.isArray(value) || !value.every((item) => typeof item === 'string')) {
          return false;
        }
      }
    }

    return true;
  };

  /**
   * Checks if the error is an Axios error with a response
   */
  const isAxiosError = (
    error: unknown,
  ): error is {
    response?: {
      data?: unknown;
      status?: number;
    };
  } => {
    return error !== null && typeof error === 'object' && 'response' in error;
  };

  // ==========================================
  // Error Extraction Functions
  // ==========================================

  /**
   * Extracts the API response data from an Axios error
   */
  const getApiResponseFromError = (error: unknown): unknown => {
    if (isAxiosError(error) && error.response?.data) {
      return error.response.data;
    }
    return null;
  };

  /**
   * Gets the HTTP status code from an error if available
   */
  const getErrorStatusCode = (error: unknown): number | null => {
    if (isAxiosError(error) && error.response?.status) {
      return error.response.status;
    }
    return null;
  };

  /**
   * Gets a user-friendly error message from any error.
   *
   * Checks for validation errors first (more specific), then the top-level
   * message, then standard Error.message, and finally falls back to the
   * provided fallback string.
   *
   * @param error The caught error (Axios error, API error, or generic Error)
   * @param fallback Optional fallback message when no specific error can be extracted
   */
  const getErrorMessage = (error: unknown, fallback?: string): string => {
    // Check if it's an API error response
    if (isAxiosError(error) && error.response?.data) {
      const data = error.response.data;

      if (isApiError(data)) {
        // Check validation errors first (more specific than top-level message)
        if (data.errors) {
          const firstFieldErrors = Object.values(data.errors)[0];
          if (firstFieldErrors && firstFieldErrors.length > 0 && firstFieldErrors[0]) {
            return firstFieldErrors[0];
          }
        }
        if (data.message) {
          return data.message;
        }
      }

      if (typeof data === 'object' && data !== null) {
        const obj = data as Record<string, unknown>;
        if (typeof obj.message === 'string') {
          return obj.message;
        }
      }
    }

    // Handle direct API error
    if (isApiError(error) && error.message) {
      return error.message;
    }

    // Fallback to standard error message
    if (error instanceof Error) {
      return error.message;
    }

    return fallback ?? 'An unknown error occurred';
  };

  /**
   * Extracts error messages from an ApiV1Response error response
   *
   * @param data The API response data
   * @returns An object with error messages or null if not an API error
   */
  const getApiErrors = (data: unknown): ApiErrorInfo | null => {
    if (!isApiError(data)) {
      return null;
    }

    const response = data;
    const result: ApiErrorInfo = {
      message: response.message,
      validationErrors: {} as Record<string, string>,
      hasValidationErrors: false,
    };

    // Extract validation errors if they exist
    if (response.errors) {
      result.hasValidationErrors = true;

      // Convert arrays of error messages to single strings (first message)
      Object.entries(response.errors).forEach(([field, messages]) => {
        result.validationErrors[field] = messages[0] || '';
      });
    }

    return result;
  };

  // ==========================================
  // Specific Error Handlers
  // ==========================================

  /**
   * Checks if the error is specifically an email not verified error
   */
  const isEmailNotVerifiedError = (error: unknown): boolean => {
    // Check direct API response
    if (isApiError(error) && error.error_type === 'email_not_verified') {
      return true;
    }

    // Check Axios error response
    if (isAxiosError(error) && error.response?.data) {
      const data = error.response.data;

      // Check if it's our API error format
      if (isApiError(data) && data.error_type === 'email_not_verified') {
        return true;
      }

      // Check for other formats
      if (typeof data === 'object' && data !== null) {
        const obj = data as Record<string, unknown>;
        return (
          obj.error_type === 'email_not_verified' ||
          (typeof obj.message === 'string' &&
            obj.message.includes('email') &&
            obj.message.includes('verified'))
        );
      }
    }

    return false;
  };

  /**
   * Handles email verification errors with appropriate UI feedback
   */
  const handleEmailVerificationError = (
    error: unknown,
    router: RouterInterface,
    notify: NotifyInterface,
    i18n: I18nInterface,
  ): boolean => {
    // Check if it's an Axios error with response
    if (isAxiosError(error) && error.response?.data) {
      const data = error.response.data;

      // Check if it's our API error format with email_not_verified error_type
      if (isApiError(data) && data.error_type === 'email_not_verified') {
        // Use the translated message from API if available, otherwise fallback to i18n
        const errorMessage = data.message || i18n.t('common.auth.emailNotVerified');
        handleVerificationNotification(errorMessage);
        return true;
      }
    }

    // Check direct API response (not wrapped in Axios error)
    if (isApiError(error) && error.error_type === 'email_not_verified') {
      const errorMessage = error.message || i18n.t('common.auth.emailNotVerified');
      handleVerificationNotification(errorMessage);
      return true;
    }

    // Legacy checks for other formats (keep for backward compatibility)
    if (isAxiosError(error) && error.response?.data) {
      const data = error.response.data as Record<string, unknown>;

      // Check for Laravel exception format with verification message
      if (typeof data.message === 'string') {
        const message = data.message;
        if (
          (message.includes('email') && message.includes('verified')) ||
          message.includes('Your email address is not verified')
        ) {
          const errorMessage = message; // Use the actual error message
          handleVerificationNotification(errorMessage);
          return true;
        }
      }

      // Check for exception type
      if (
        typeof data.exception === 'string' &&
        data.exception.includes('HttpException') &&
        typeof data.message === 'string' &&
        data.message.includes('not verified')
      ) {
        handleVerificationNotification(data.message);
        return true;
      }
    }

    // Direct check for objects that might be the error response itself
    if (error && typeof error === 'object') {
      const data = error as Record<string, unknown>;

      if (typeof data.message === 'string') {
        const message = data.message;
        if (message.includes('Your email address is not verified')) {
          handleVerificationNotification(message);
          return true;
        }
      }
    }

    return false;

    // Helper function to avoid code duplication
    function handleVerificationNotification(message: string) {
      // Show verification required notification with the actual error message
      notify({
        color: 'grey-6',
        message: message,
        actions: [
          {
            label: i18n.t('common.auth.resendVerification'),
            color: 'white',
            handler: () => {
              void router.push('/send-verification-email');
            },
          },
        ],
        timeout: 10000,
      });

      // Redirect to verification page
      void router.push('/send-verification-email');
    }
  };

  // ==========================================
  // Response Validation Functions
  // ==========================================

  /**
   * Validates a list response with pagination
   *
   * @param response The Axios response object
   * @returns true if valid, false otherwise
   */
  const isValidListResponse = (response: unknown): boolean => {
    if (!response || typeof response !== 'object') {
      return false;
    }

    const resp = response as Record<string, unknown>;

    // Check if response.data exists
    if (!resp.data || typeof resp.data !== 'object') {
      return false;
    }

    const data = resp.data as Record<string, unknown>;

    // Check for success status (optional for some endpoints)
    // Some endpoints return status, some don't
    if (data.status && data.status !== 'success') {
      return false;
    }

    // Check if data.data is an array (can be empty)
    if (!Array.isArray(data.data)) {
      return false;
    }

    // Check if pagination exists in either location:
    // - response.data.pagination (projects, schedules, properties)
    // - response.data.meta (teams, organizations, tasks)
    const hasPagination =
      (data.pagination && typeof data.pagination === 'object') ||
      (data.meta && typeof data.meta === 'object');

    if (!hasPagination) {
      return false;
    }

    return true;
  };

  /**
   * Extracts list data from a validated response
   *
   * @param response The Axios response object
   * @returns Array of data items or null if invalid
   */
  const extractListData = <T = unknown>(response: unknown): T[] | null => {
    if (!isValidListResponse(response)) {
      return null;
    }

    const resp = response as Record<string, unknown>;
    const data = resp.data as Record<string, unknown>;

    return data.data as T[];
  };

  /**
   * Extracts pagination metadata from a validated response
   *
   * @param response The Axios response object
   * @returns Pagination object or null if invalid
   */
  const extractPaginationMeta = (response: unknown): PaginationMeta | null => {
    if (!isValidListResponse(response)) {
      return null;
    }

    const resp = response as Record<string, unknown>;
    const data = resp.data as Record<string, unknown>;

    // Try to get pagination from either location:
    // 1. response.data.pagination (projects, schedules, properties)
    // 2. response.data.meta (teams, organizations, tasks)
    let pagination: Record<string, unknown>;

    if (data.pagination && typeof data.pagination === 'object') {
      pagination = data.pagination as Record<string, unknown>;
    } else if (data.meta && typeof data.meta === 'object') {
      pagination = data.meta as Record<string, unknown>;
    } else {
      return null;
    }

    return {
      current_page: pagination.current_page as number,
      last_page: pagination.last_page as number,
      per_page: pagination.per_page as number,
      total: pagination.total as number,
      from: pagination.from as number | null,
      to: pagination.to as number | null,
    };
  };

  /**
   * Validates and extracts both list data and pagination in one call
   *
   * @param response The Axios response object
   * @returns Object with data and pagination, or null if invalid
   */
  const extractListResponse = <T = unknown>(response: unknown): ListResponse<T> | null => {
    const data = extractListData<T>(response);
    const pagination = extractPaginationMeta(response);

    if (data === null || pagination === null) {
      return null;
    }

    return { data, pagination };
  };

  // ==========================================
  // General Error Handler
  // ==========================================

  /**
   * Comprehensive function to handle all API errors
   */
  const handleApiError = (
    error: unknown,
    router: RouterInterface,
    notify: NotifyInterface,
    i18n: I18nInterface,
  ): void => {
    // Check for email verification error first
    if (handleEmailVerificationError(error, router, notify, i18n)) {
      return; // Email verification error was handled
    }

    // Handle validation errors
    const apiData = getApiResponseFromError(error);
    if (isApiError(apiData) && apiData.errors) {
      const errorInfo = getApiErrors(apiData);
      if (errorInfo?.hasValidationErrors) {
        notify({
          color: 'negative',
          message: errorInfo.message || i18n.t('common.errors.validationFailed'),
          caption: Object.values(errorInfo.validationErrors).join(', '),
          timeout: 5000,
        });
        return;
      }
    }

    // Handle other errors
    notify({
      color: 'negative',
      message: getErrorMessage(error),
      timeout: 5000,
    });
  };

  // Return all functions for use in components
  return {
    // Type Guards
    isApiError,
    isApiV1Response,
    isAxiosError,

    // Error Extraction
    getApiResponseFromError,
    getErrorStatusCode,
    getErrorMessage,
    getApiErrors,

    // Response Validation
    isValidListResponse,
    extractListData,
    extractPaginationMeta,
    extractListResponse,

    // Specific Error Handlers
    isEmailNotVerifiedError,
    handleEmailVerificationError,

    // General Error Handler
    handleApiError,
  };
}

// Export types
export type {
  ApiV1Response,
  ApiErrorInfo,
  RouterInterface,
  NotifyInterface,
  I18nInterface,
  PaginationMeta,
  ListResponse,
} from '@shared/core/types/api';
